export interface AdfParserConfig {
	/**
	 * When parsing an object and encountering a `]` instead of a property key,
	 * auto-close the object (inferring a missing `}`) and let the parent array handle the `]`.
	 * This handles malformed JSON like: {"type": "foo", "attrs": {}] (missing closing brace)
	 * @default false
	 */
	autoCloseObjectOnRightBracket?: boolean;
	/**
	/**
	 * When enabled, the parser will attempt to filter out invalid marks if they do not have required attributes or have unknown types.
	 * This ensures that invalid marks are not included in the final parsed ADF, which could cause issues for downstream consumers that expect marks to be valid according to the schema.
	 * @default false
	 */
	filterInvalidMarks?: boolean;
	/**
	 * When enabled, attempts to correct unknown node types by finding the closest
	 * valid type in the schema using Levenshtein distance (e.g. "paragrap" → "paragraph").
	 * Only applies when the node type is unrecognized and a parent context is available.
	 * @default false
	 */
	fuzzyNodeTypeMatching?: boolean;
	/**
	 * When enabled, unknown/unexpected characters in the input are ignored instead of
	 * throwing an error. A repair event is emitted for each ignored character.
	 * This enables lenient parsing of malformed JSON with stray characters.
	 * @default false
	 */
	ignoreUnknownCharacters?: boolean;
	/**
	 * Parse unquoted string values (lenient mode for LLM outputs).
	 * Allows parsing: {"type": paragraph} instead of {"type": "paragraph"}
	 * @default false
	 */
	lenientStringParsing?: boolean;
	/**
	 * When a block node (panel, table, etc.) is invalid in its current context,
	 * lift it to the nearest valid ancestor instead of skipping it.
	 * - For simple containers (panel, blockquote): close the container and insert lifted node immediately after
	 * - For compound nodes (list, table): accumulate and insert at the end of the document
	 * @default false
	 */
	liftInvalidBlockNodes?: boolean;
	/**
	 * When enabled, throws structured error classes (AdfParseError hierarchy) instead of
	 * generic Error objects. This provides better error information including position,
	 * expected/actual tokens, and error type classification.
	 * @default false
	 */
	useTypedErrors?: boolean;
	/**
	 * Use the refactored unified content array parsing implementation.
	 * When false (default), uses the original two-path implementation (V1).
	 * When true, uses the unified implementation that reduces code duplication (V2).
	 * Both produce identical results; this flag exists to allow safe rollout.
	 * @default false
	 */
	useUnifiedContentParsing?: boolean;
}

// ADF Node Types
// Represents any ADF node that can be at the root level
export interface AdfNode {
	[key: string]: unknown; // Allow additional properties
	attrs?: Record<string, unknown>;
	content?: Array<ContentNode>;
	marks?: Mark[];
	text?: string;
	type: string;
	version?: number;
}

export interface ContentNode {
	[key: string]: unknown; // Allow additional properties
	attrs?: Record<string, unknown>;
	content?: Array<ContentNode>;
	marks?: Mark[];
	text?: string;
	type: string;
}

export interface Mark {
	attrs?: Record<string, unknown>;
	type: string;
}

export interface Repair {
	context: string;
	expansion?: number;
	from: string;
	// For unwrap repairs: true if the unwrapped node is preserved (moved up), false if removed
	nodePreserved?: boolean;
	to: string;
	type: 'skip' | 'skip_empty_text' | 'infer' | 'duplicate_key' | 'wrap' | 'unwrap' | 'lift';
}

// Symbol to mark nodes that should be lifted to parent level
export const LIFT_MARKER = Symbol('lift');

export interface LiftedNode {
	/** If true, close the current container and lift remaining siblings too */
	closeContainer?: boolean;
	[LIFT_MARKER]: true;
	/** Single node or multiple nodes to lift */
	node: ContentNode | ContentNode[];
}

export function isLiftedNode(value: unknown): value is LiftedNode {
	return typeof value === 'object' && value !== null && LIFT_MARKER in value;
}

// Tokenizer Types
export type TokenType =
	| 'STRING'
	| 'NUMBER'
	| 'BOOLEAN'
	| 'NULL'
	| 'LEFT_BRACE'
	| 'RIGHT_BRACE'
	| 'LEFT_BRACKET'
	| 'RIGHT_BRACKET'
	| 'COLON'
	| 'COMMA'
	| 'EOF';

type BaseToken<T extends TokenType, V extends string = string> = {
	position: number;
	type: T;
	value: V;
};

export type Token =
	| BaseToken<'STRING', string>
	| BaseToken<'NUMBER', string>
	| BaseToken<'BOOLEAN', 'true' | 'false'>
	| BaseToken<'NULL', 'null'>
	| BaseToken<'LEFT_BRACE', '{'>
	| BaseToken<'RIGHT_BRACE', '}'>
	| BaseToken<'LEFT_BRACKET', '['>
	| BaseToken<'RIGHT_BRACKET', ']'>
	| BaseToken<'COLON', ':'>
	| BaseToken<'COMMA', ','>
	| BaseToken<'EOF', ''>;

export type TokenOfType<T extends TokenType> = Extract<Token, { type: T }>;

export type PrimitiveValue = string | number | boolean | null | undefined;

// Parser-specific types
export type NodeHandle = string;

export interface StreamingCallbacks {
	/**
	 * Called when the node is fully parsed with all its data
	 */
	onNodeComplete?: (handle: NodeHandle, node: ContentNode) => void;

	/**
	 * Called immediately when a node's "type" is discovered
	 * Return a handle to track this node for future updates
	 */
	onNodeStart?: (nodeType: string, parentType: string | undefined) => NodeHandle;

	/**
	 * Called as properties are discovered during parsing (text, attrs, marks, etc)
	 */
	onNodeUpdate?: (handle: NodeHandle, updates: Partial<ContentNode>) => void;

	/**
	 * Called when repairs are applied
	 */
	onRepair?: (repair: Repair) => void;
}
